import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  useTheme,
  IconButton,
  Dialog,
  DialogContent,
  Chip,
} from '@mui/material';
import {
  PlayCircleOutline as PlayIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  OndemandVideo as VideoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

interface TutorialVideo {
  title: string;
  description: string;
  url: string;
  tag: string;
}

const tutorials: TutorialVideo[] = [
  {
    title: 'Overview Tab',
    description: 'Learn how to navigate the main overview tab and see your key metrics at a glance.',
    url: 'https://marshall-3145141.s3.us-east-1.amazonaws.com/Overview+Tab.mp4',
    tag: 'Getting Started',
  },
  {
    title: 'Other Tabs',
    description: 'Explore the additional tabs and features available throughout the system.',
    url: 'https://marshall-3145141.s3.us-east-1.amazonaws.com/Other+Tabs.mp4',
    tag: 'Navigation',
  },
  {
    title: 'Settings Tab',
    description: 'Configure your dashboard settings and customize your experience.',
    url: 'https://marshall-3145141.s3.us-east-1.amazonaws.com/Settings+Tab.mp4',
    tag: 'Configuration',
  },
];

const Tutorials: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [activeVideo, setActiveVideo] = useState<TutorialVideo | null>(null);
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3, py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton
          onClick={() => navigate('/leaderdashboard')}
          sx={{
            mr: 2,
            background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(102,126,234,0.08)',
            '&:hover': {
              background: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(102,126,234,0.15)',
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              background: isDark
                ? 'linear-gradient(135deg, #ffffff 0%, #b0b0b0 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Video Tutorials
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            Watch these short videos to learn how to use the system
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 3,
        }}
      >
        {tutorials.map((tutorial) => (
          <Card
            key={tutorial.title}
            elevation={0}
            sx={{
              borderRadius: 3,
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(102,126,234,0.15)'}`,
              background: isDark
                ? 'linear-gradient(135deg, #1e1e1e 0%, #2a2a2a 100%)'
                : 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: isDark
                  ? '0 12px 40px rgba(0,0,0,0.4)'
                  : '0 12px 40px rgba(102,126,234,0.2)',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.2)' : 'rgba(102,126,234,0.3)'}`,
              },
            }}
          >
            <CardActionArea onClick={() => setActiveVideo(tutorial)}>
              <Box
                sx={{
                  position: 'relative',
                  height: 180,
                  background: isDark
                    ? 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 100%)'
                    : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    opacity: 0.1,
                    backgroundImage:
                      'radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px)',
                    backgroundSize: '30px 30px',
                  }}
                />
                <PlayIcon
                  sx={{
                    fontSize: 72,
                    color: 'rgba(255,255,255,0.9)',
                    filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))',
                    transition: 'transform 0.3s ease',
                    '.MuiCardActionArea-root:hover &': {
                      transform: 'scale(1.15)',
                    },
                  }}
                />
              </Box>
              <CardContent sx={{ p: 2.5 }}>
                <Chip
                  label={tutorial.tag}
                  size="small"
                  sx={{
                    mb: 1.5,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    background: isDark
                      ? 'rgba(102,126,234,0.2)'
                      : 'rgba(102,126,234,0.1)',
                    color: isDark ? '#a0b4ff' : '#667eea',
                    border: 'none',
                  }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                  {tutorial.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {tutorial.description}
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>

      <Dialog
        open={!!activeVideo}
        onClose={() => setActiveVideo(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: isDark ? '#1a1a1a' : '#ffffff',
            overflow: 'hidden',
          },
        }}
      >
        {activeVideo && (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 3,
                py: 2,
                borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <VideoIcon sx={{ color: isDark ? '#a0b4ff' : '#667eea' }} />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {activeVideo.title}
                </Typography>
              </Box>
              <IconButton onClick={() => setActiveVideo(null)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
            <DialogContent sx={{ p: 0 }}>
              <video
                controls
                autoPlay
                style={{ width: '100%', display: 'block' }}
                src={activeVideo.url}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Tutorials;
